// components/Calendar.js
import React, { useState, useEffect, useCallback } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import { supabase } from "../supabaseClient";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";

import moment from "moment";
import "moment/locale/ko";
import "react-big-calendar/lib/css/react-big-calendar.css";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";

moment.locale("ko");

const localizer = momentLocalizer(moment);

const MyCalendar = ({ userId, reloadTrigger, onDateSelect }) => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null); // 🔹 선택한 예약 정보

  // 🔹 1. 예약 가져오기 함수 (useCallback)
  const fetchReservations = useCallback(async () => {
    const { data, error } = await supabase
      .from("reservations")
      .select("*")
      .eq("user_id", userId)
      .order("start_time", { ascending: true });

    if (!error && data) {
      const mappedEvents = data.map((item) => ({
        id: item.id,
        title: item.title,
        description: item.description,
        start: new Date(item.start_time),
        end: new Date(item.end_time),
        alarm_time: item.alarm_time,
        color: item.color || "#3f51b5", // 🔹 기본 색상 적용
      }));
      setEvents(mappedEvents);
    }
  }, [userId]);

  // 🔹 2. 삭제 함수 (fetchReservations 사용 가능)
  const handleDeleteReservation = async () => {
    if (!selectedEvent?.id) return;

    const { error } = await supabase
      .from("reservations")
      .delete()
      .eq("id", selectedEvent.id);

    if (!error) {
      setSelectedEvent(null);
      fetchReservations(); // ✅ 최신 리스트 다시 불러오기
    } else {
      alert("삭제 실패: " + error.message);
    }
  };

  // 🔹 3. useEffect로 초기 예약 로딩
  useEffect(() => {
    if (userId) {
      fetchReservations();
    }
  }, [userId, reloadTrigger, fetchReservations]);

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
  };

  const handleCloseDialog = () => {
    setSelectedEvent(null);
  };

  // 📌 날짜 셀 클릭 시
  const handleSlotSelect = (slotInfo) => {
    if (onDateSelect) {
      onDateSelect(slotInfo.start); // 🔹 날짜 클릭 시 부모에게 전달
    }
  };
  
  return (
    <Box sx={{ mt: 0 }}>
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height: 500 }}
        views={["month", "week", "day"]}
        defaultView="month"
        selectable
        onSelectSlot={handleSlotSelect}
        onSelectEvent={handleSelectEvent}
        eventPropGetter={(event) => ({
          style: {
            backgroundColor: event.color,
            color: "#fff", // 글자색 흰색으로
            borderRadius: "4px",
            border: "none",
            padding: "2px 4px",
          },
        })}
      />

      {/* 🔹 예약 상세 팝업 */}
      <Dialog open={!!selectedEvent} onClose={handleCloseDialog}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {selectedEvent?.title}
          {selectedEvent?.alarm_time && (
            <NotificationsActiveIcon color="warning" fontSize="small" />
          )}
        </DialogTitle>

        <DialogContent dividers>
          <DialogContentText>
            {selectedEvent?.description || "예약 상세 정보가 없습니다."}
          </DialogContentText>
          <Typography variant="body2" sx={{ mt: 2 }}>
            시작: {moment(selectedEvent?.start).format("YYYY-MM-DD HH:mm")}
          </Typography>
          <Typography variant="body2">
            종료: {moment(selectedEvent?.end).format("YYYY-MM-DD HH:mm")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>닫기</Button>
          <Button color="error" onClick={handleDeleteReservation}>삭제</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyCalendar;
